import './style.css';

import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { Alert } from '../shared/components/alert/Alert';
import { Button } from '../shared/components/button/Button';
import GoogleAuthButton from '../shared/components/logIn/singleSignOn/GoogleAuthButton';
import MicrosoftAuthButton from '../shared/components/logIn/singleSignOn/MicrosoftAuthButton';
import OpenIDAuthButton from '../shared/components/logIn/singleSignOn/OpenIDAuthButton';
import LoadingSpinner from '../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../shared/context/auth-context';
import { useHttpClient } from '../shared/hooks/http-hook';
import { useUserData } from '../shared/hooks/useUserData';
import { getErrorMessage } from '../shared/util/errors';
import { handleEmailChange } from '../shared/util/handleValueChange';
import { RightPanelContainer } from './layout/RightPanelContainer';
import { PasswordInput } from './PasswordInput';
import Passcode from './signup/passcode/Passcode';

function Login(props) {
  const location = useLocation();
  const { t } = useTranslation();

  const auth = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttpClient();

  const [isCompLoading, setCompLoading] = useState(false);
  const [errorCompMessage, setCompErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState<string | undefined>();
  const [password, setPassword] = useState<string | undefined>();
  const [userId, setUserId] = useState();
  const [name, setName] = useState();
  const [clientId, setClientId] = useState<string | boolean | undefined>(false);
  const [tempAccessToken, setTempAccessToken] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { userData } = useUserData();

  const navigate = useNavigate();

  useEffect(() => {
    const rightContainer = document.getElementById('right_container');
    if (rightContainer) {
      // Find the first focusable element within the right_container
      const focusableElements = rightContainer.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [userId]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!auth.isLoggedIn && !location.state) {
      if (userData && userData.isStandardAuthActive !== undefined) {
        setClientId(userData.clientid);
      } else {
        navigate('/');
      }
    }
  }, [auth.isLoggedIn, location, navigate, props, userData]);

  const signInHandler = async (event) => {
    event.preventDefault();

    // Clear previous errors
    setBackendError(null);
    setValidationError(null);

    // Validate email
    if (emailError) {
      setValidationError(t('signin.email.error'));
      return;
    }

    // Validate that fields are not empty
    if (!email?.trim() || !password?.trim()) {
      setValidationError(t('R052'));
      return;
    }

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/signin`,
        'POST',
        JSON.stringify({
          email: email.toLowerCase(),
          password,
          platform: 'web',
        })
      );
      if (response && response.user._id) {
        setUserId(response.user._id);
        setName(response.user.name);

        setTempAccessToken(response.tempToken);
        if (!response.user.name || !response.user.passcode) {
          response.user.email = email;
          response.user.password = password;
          navigate('/signup', {
            state: response,
          });
        }
      }
    } catch (err) {
      setBackendError(getErrorMessage(err, t('signin.error.general')));
    }
  };

  const handleResponseSSO = async (response) => {
    if (response) {
      setUserId(response.user._id);
      setName(response.user.name);

      if (
        (response.user.completed_registration === undefined ||
          response.user.completed_registration === true) &&
        response.token
      ) {
        await auth.login(
          response.user._id,
          response.token,
          response.user.name,
          response?.expirationDate,
          response.user.access,
          response.user.image,
          response.user.tags,
          response.clientName,
          response.user.nps_status,
          response.user.auth_type,
          response.user.email,
          response.user.language,
          false, // hub
          false, // fnfDisabled
          response.clientInfo // Pass clientInfo to update client data in local storage
        );

        // check if assessment complete or redirected from another page
        const storedUrl = sessionStorage.getItem('DirectLink');
        sessionStorage.removeItem('DirectLink');
        if (storedUrl && response.user.tags.length > 1) {
          navigate(storedUrl);
        } else if (response.user.tags.length > 1) {
          navigate('/for-you');
        } else {
          navigate('/assessment');
        }
      } else {
        navigate('/signup', {
          state: response,
        });
      }
    }
  };

  const signInWithPasscode = async (code) => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/checkPasscode`,
        'POST',
        JSON.stringify({
          tempToken: tempAccessToken,
          passcode: code,
          platform: 'web',
        })
      );

      if (response && response.token) {
        await auth.login(
          response.user._id,
          response.token,
          response.user.name,
          response?.expirationDate,
          response.user.access,
          response.user.image,
          response.user.tags,
          response.clientName,
          response.user.nps_status,
          response.user.auth_type,
          response.user.email,
          response.user.language,
          response?.hub,
          false, // fnfDisabled
          response.clientInfo // Pass clientInfo to update client data in local storage
        );

        // check if assessment complete
        const storedUrl = sessionStorage.getItem('DirectLink');
        sessionStorage.removeItem('DirectLink');

        if (storedUrl) {
          navigate(storedUrl);
        } else if (response.user.tags.length > 1) {
          navigate('/for-you');
        } else {
          navigate('/assessment');
        }
      }
    } catch (err) {
      setErrorMessage(getErrorMessage(err, t('signin.error.general')));
    }
  };

  const handleResetDataError = () => {
    setValidationError(null);
    setBackendError(null);
    setSuccessMessage(null);
  };

  const handleEmailInputChange = (e) => {
    handleResetDataError();
    handleEmailChange(e, setEmail, setEmailError);
  };

  return (
    <RightPanelContainer type="signin">
      <h1>{t('R011')}</h1>
      {!userId && userData?.isStandardAuthActive && (
        <>
          <form onSubmit={signInHandler}>
            <label className="not_logged_label" htmlFor="email">
              {t('R023')}
            </label>
            {successMessage && (
              <Alert
                type="success"
                message={successMessage}
                id="success-message"
                isVisible={!!successMessage}
                isAssessive
              />
            )}
            <input
              type="email"
              className="code_input"
              id="email"
              name="email"
              onChange={(e) => handleEmailInputChange(e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading && !isCompLoading) {
                  e.preventDefault();
                  signInHandler(e);
                }
              }}
              data-testid="email-input"
              required
            />

            {validationError && (
              <Alert
                type="error"
                message={validationError}
                id="validation-error"
                isVisible={!!validationError}
                isAssessive
                dataTestId="login-error"
              />
            )}

            {backendError && (
              <Alert
                type="error"
                message={backendError}
                id="backend-error"
                isVisible={!!backendError}
                isAssessive
                dataTestId="login-error"
              />
            )}

            <PasswordInput
              visible
              handlePassword={(value) => setPassword(value)}
              resetDataError={handleResetDataError}
              onEnterKeyPress={(e) => {
                if (!isLoading && !isCompLoading) {
                  const syntheticEvent = {
                    preventDefault: () => {},
                  } as React.FormEvent;
                  signInHandler(syntheticEvent);
                }
              }}
              data-testid="password-input"
            />

            {(!isLoading || !isCompLoading) && (
              <Button type="submit" id="continue" dataTestId="signin-button">
                {t('R011')}
              </Button>
            )}
          </form>

          {userData?.isStandardAuthActive && (
            <a
              style={{
                margin: '10px 0px 0px 0px',
                fontFamily: 'futura-pt-book',
              }}
              className="signin"
              href="/reset"
            >
              {name ? t('R047') : t('R014')}
            </a>
          )}
        </>
      )}

      <div className="sso_container">
        {!userId &&
          userData?.isStandardAuthActive &&
          clientId &&
          (!isLoading || !isCompLoading) &&
          userData?.isStandardAuthActive &&
          (userData?.isMicrosoftAuthActive ||
            userData?.isGoogleAuthActive ||
            userData?.singleSignOn.length > 0) && <p className="or">{t('R071')}</p>}

        {userId && name && (
          <>
            <p>{t('R043')}</p>
            <Passcode handleContinue={signInWithPasscode} errorMessage={errorMessage} />
          </>
        )}
        {errorCompMessage && (
          <Alert
            type="error"
            message={errorCompMessage}
            id="error-comp-message"
            isVisible={!!errorCompMessage}
            isAssessive
          />
        )}
        {isLoading && isCompLoading && <LoadingSpinner asOverlay />}
        {(!isLoading || !isCompLoading) &&
          userData?.isMicrosoftAuthActive &&
          !userId &&
          clientId && (
            <MicrosoftAuthButton
              handleResponse={handleResponseSSO}
              fandfUser={props.fandfUser}
              clientid={clientId}
              setErrorMessage={setCompErrorMessage}
              setCompLoading={setCompLoading}
              signIn
              dataTestId="microsoft-auth-button"
            />
          )}
        {(!isLoading || !isCompLoading) && userData?.isGoogleAuthActive && !userId && clientId && (
          <GoogleAuthButton
            handleResponse={handleResponseSSO}
            fandfUser={props.fandfUser}
            clientid={clientId}
            setErrorMessage={setCompErrorMessage}
            setCompLoading={setCompLoading}
            dataTestId="google-auth-button"
          />
        )}
        {userData?.singleSignOn?.map((sso) => {
          if ((!isLoading || !isCompLoading) && !userId && clientId) {
            return (
              <OpenIDAuthButton
                key={sso._id}
                configurationName={sso.type}
                ssoId={sso.id}
                redirectUri={`${window.location.origin}/sign-in/callback`}
                scope={sso.scope}
                authority={sso.issuer}
                secret={sso.secret}
                clientId={clientId}
                setErrorMessage={setCompErrorMessage}
                handleResponse={handleResponseSSO}
                setCompLoading={setCompLoading}
              />
            );
          }

          return null;
        })}
      </div>
    </RightPanelContainer>
  );
}
export default Login;
